import React from "react";
import { useViewport } from "../../context/ViewportContext";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { breakpoints } from "../../styles/breakpoints";
import { useSlideOutStore } from "../../stores/slices/slideOutSlice";
import useStore from "../../stores/store";
import { SceneNames } from "../../constants/sceneNames";
import { TransitionType } from "../../constants/transitions";

const StyledLogo = styled.svg`
  .logo--db--fill {
    fill: ${({ theme, $isSlideOutPresent }) =>
      $isSlideOutPresent ? theme.primaryColor : theme.primaryColor};
    transition: fill 0.3s ease-in-out;
  }
  &.logo {
    margin: 0 auto;
    ${({ $viewport }) => {
      const { width, isLandscape } = $viewport;
      if (width <= breakpoints.mobileS) {
        return `
          width: ${isLandscape ? "1.5rem" : "2rem"};
          height: ${isLandscape ? "1.5rem" : "2rem"};
        `;
      }
      if (width <= breakpoints.mobileL) {
        return `
          width: ${isLandscape ? "1.75rem" : "2rem"};
          height: ${isLandscape ? "1.75rem" : "2rem"};
        `;
      }
      if (width <= breakpoints.tabletS) {
        return `
          width: ${isLandscape ? "2rem" : "2rem"};
          height: ${isLandscape ? "2rem" : "2rem"};
        `;
      }
      if (width <= breakpoints.tabletM) {
        return `
          width: ${isLandscape ? "2rem" : "2.5rem"};
          height: ${isLandscape ? "2rem" : "2.5rem"};
        `;
      }
      if (width <= breakpoints.tabletL) {
        return `
          width: ${isLandscape ? "1.75rem" : "2.5rem"};
          height: ${isLandscape ? "1.75rem" : "2.5rem"};
        `;
      }
      return `
        width: 3.25rem;
        height: 3.25rem;
      `;
    }}
  }
`;

const StyledLogoWrapper = styled(Link)`
  display: grid;
  grid-column: 1;
  grid-row: 1;
  z-index: 990;
  border-bottom: 1px solid ${({ theme }) => theme.primaryColor};
  align-content: center;
  width: 100%;
  height: 100%;
  pointer-events: auto;

  ${({ $viewport }) => {
    const { width, isLandscape } = $viewport;
    if (width <= breakpoints.mobileS) {
      return `
        height: ${isLandscape ? "95%" : "98%"};
      `;
    }
    if (width <= breakpoints.mobileL) {
      return `
        height: ${isLandscape ? "96%" : "99%"};
      `;
    }
    if (width <= breakpoints.tabletM) {
      return `
        height: ${isLandscape ? "97%" : "100%"};
      `;
    }
    return `
      height: 100%;
    `;
  }}
`;

function Logo() {
  const { theme } = useStore();
  const viewport = useViewport();
  const startTransition = useStore((state) => state.startTransition);

  const { isPresent: isSlideOutPresent, isCollapsed: isSlideOutCollapsed } =
    useSlideOutStore();

  const handleClick = (e) => {
    // Start the transition to the default scene
    startTransition(SceneNames.DODECAHEDRON, TransitionType.FADE);
  };

  return (
    <StyledLogoWrapper
      onClick={handleClick}
      className="logoWrapper"
      theme={theme}
      to={"/"}
      $viewport={viewport}
      $isSlideOutPresent={isSlideOutPresent}
      $isSlideOutCollapsed={isSlideOutCollapsed}
    >
      <StyledLogo
        className="logo"
        viewBox="0 0 38.49 33.55"
        theme={theme}
        $viewport={viewport}
        $isSlideOutPresent={isSlideOutPresent}
        $isSlideOutCollapsed={isSlideOutCollapsed}
      >
        <path
          className="logo--db--fill"
          d="M16.52 30.72c-.06 0-.13 0-.20-.01-.62-.08-1.1-.53-1.31-1.19-.52.14-1.05.21-1.57.21-1.91 0-3.81-.99-5.2-2.71C6.61 25 5.9 22.31 6.39 20c.45-2.64 2.08-5.97 5.18-6.58.15-.02.27-.03.39-.03 1.2 0 2.19.86 2.83 1.63l-.24-7.44c-.03-.43.07-1.04.45-1.44.23-.25.53-.38.86-.38.2 0 .41.05.63.15.61.35.72 1.06.8 1.53l.02.1c.2 1.53.28 3.07.35 4.56l.2 3.25c.4 4.23.31 8.54.23 12.69l-.02 1.06c-.02.91-.69 1.63-1.54 1.63zm-4.41-13.9c-1.92 1.43-2.33 4.31-1.71 6.34.28.91 1.19 3.04 3.65 3.04h0c.2 0 .41-.02.62-.04-.25-2.73-.77-6.12-2.38-9.06h0s-.06-.08-.11-.17c-.02-.03-.04-.07-.07-.11zm9.36 13.72c-.07 0-.14 0-.22-.01h-.06l-.06-.03c-.53-.2-.88-.7-.92-1.34-.03-.49-.13-.97-.24-1.48l-.22-1.22c-.48-3.9-.25-7.88-.03-11.72.17-2.97.35-6.04.19-9v-.25l.17-.16c.32-.28.64-.42.95-.42a1.05 1.05 0 0 1 .79.36c.75.8.81 2.8.78 3.25l-.11 5.93c1.01-.94 2.13-1.41 3.34-1.41 1.81 0 3.7 1.11 5.21 3.06 1.86 2.41 2.68 5.46 2.08 7.77-.56 2.95-3.21 5.03-6.45 5.03a7.08 7.08 0 0 1-3.72-1.05v.23c-.01.92-.04 2.46-1.5 2.46zm1.77-5.83c.77.2 1.94.43 3.08.43 3.33 0 3.35-1.97 3.36-2.62-.15-2.4-1.48-6.28-4.4-6.37-1.82.49-1.89 3.95-1.94 6.48-.02.79-.03 1.5-.09 2.08zM2.92 33.54c-.39-.03-.68-.13-.91-.33-.7-.8-.69-1.82-.68-2.71v-.49l-.05-4.18-.15-7.01C.91 13.9.57 8.86 0 2.47c-.04-.41.07-.77.31-1.04.3-.34.78-.54 1.4-.57C2.93.8 4.92.77 6.24.76A27.17 27.17 0 0 0 7.43.74L8.89.68l3.1-.09c.73 0 1.37.02 1.97.07h.11c.58.06 1.56.14 1.88 1.07a1.56 1.56 0 0 1-.2 1.39 1.13 1.13 0 0 1-.92.49h-.74c-1.46 0-3.04.04-4.56.09l-4.66.1c-.63 0-1.25 0-1.85-.03l.26 4.89c.4 7.5.79 14.59.73 22.04v.11h1.22l4.21-.05 4.19-.05h.3c.15 0 .26.05.33.07.61.22.88.8.69 1.34-.32.68-.58.98-1.28 1.09-3.56.28-6.24.34-9.41.34H2.9zm25.29-1.26a42.62 42.62 0 0 1-2.55-.08c-.16 0-.32-.01-.5-.06-.52 0-.92-.37-.99-.87a.97.97 0 0 1 .67-1.1l.23-.05c.13-.04.24-.07.36-.08 2.43-.37 4.46-.41 6.62-.44l3.63-.11c-.1-9.59-.03-13.77.24-22.96 0-.11-.1-4-.1-4-.92-.04-1.79-.06-2.36-.06h-1.09c-3.03 0-5.66.01-9.13.38-.11.01-.17.02-.24.02h-.14c-.12-.02-.23-.06-.34-.11-.37-.17-.62-.53-.68-.99-.07-.55.15-1.13.54-1.44.31-.25.65-.28.91-.28h.32c.61.02 1.57.03 2.58.03l3.73-.02 6.17-.05h.84c.48 0 .89.33 1.12.65.31.42.33.92.35 1.31.07 1.66.06 2.83.05 4.18v2.16 1.12l-.29 16.58-.13 4.43a1.38 1.38 0 0 1-.96 1.43l-.06.02h-.06c-1.36.04-2.62.12-3.83.2-1.66.11-3.22.2-4.92.2z"
        />
      </StyledLogo>
    </StyledLogoWrapper>
  );
}

export default Logo;
